import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { useTokenManagement } from './hooks/useTokenManagement';
import ContactSupportButton from './components/support/ContactSupportButton';
import { trackPageView } from './utils/analytics';

// Pages
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import AddSubjectPage from './pages/dashboard/AddSubjectPage';
import AddManualSyllabusPage from './pages/dashboard/AddManualSyllabusPage';
import LessonPage from './pages/lesson/LessonPage';
import LessonHistoryPage from './pages/history/LessonHistoryPage';
import LessonHistoryViewPage from './pages/history/LessonHistoryViewPage';
import ProgressReportsPage from './pages/progress/ProgressReportsPage';
import ProgressReportDetailPage from './pages/progress/ProgressReportDetailPage';
import Analytics from './pages/admin/Analytics';
import ProfilePage from './pages/settings/ProfilePage';
import SubscriptionSettings from './pages/settings/SubscriptionSettings';
import SubscriptionSuccess from './pages/subscription/SubscriptionSuccess';
import SubscriptionCanceled from './pages/subscription/SubscriptionCanceled';

function usePageTracking() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
}

function AppContent() {
  // Set up token management listeners and periodic checks
  useTokenManagement();
  usePageTracking();
  const { isAuthenticated } = useAuth();

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/add-subject"
          element={
            <ProtectedRoute>
              <AddSubjectPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/add-syllabus/:courseId"
          element={
            <ProtectedRoute>
              <AddManualSyllabusPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lesson"
          element={
            <ProtectedRoute>
              <LessonPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute>
              <LessonHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/history/:lessonId"
          element={
            <ProtectedRoute>
              <LessonHistoryViewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/progress-reports"
          element={
            <ProtectedRoute>
              <ProgressReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/progress-reports/:reportId"
          element={
            <ProtectedRoute>
              <ProgressReportDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute>
              <Analytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/subscription"
          element={
            <ProtectedRoute>
              <SubscriptionSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription/success"
          element={
            <ProtectedRoute>
              <SubscriptionSuccess />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription/canceled"
          element={
            <ProtectedRoute>
              <SubscriptionCanceled />
            </ProtectedRoute>
          }
        />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>

      {/* Contact Support Button - only show when authenticated */}
      {isAuthenticated && <ContactSupportButton />}
    </>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
